var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import './element-address.js';
import { postFeedback } from '../apis/api-common.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import { divider, externalLinkSvg } from '../helpers/elements.js';
import './components/element-input.js';
import './components/element-file-upload.js';
import './components/element-line-checkbox.js';
import './components/element-button.js';
import { classMap } from 'lit/directives/class-map.js';
import { modal$, season$ } from '../stores/store-main.js';
import { isEmailValid } from '../helpers/string.js';
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';
import { getLanguageCode } from '../i18n.js';
import { CAPTCHA_SITEKEY } from '../envs.js';
import { Router } from '@vaadin/router';
import { createSeasonURL } from '../urlHandling/urlCreate.js';
const STYLE = {
    gridContainer: 'grid grid-cols-4 md:grid-cols-8 mdxx:grid-cols-12',
    gridElement: 'col-span-full md:col-[1_/_span_8] mdxx:col-[1_/_span_8] xxl:col-[3_/_span_6]',
};
const ADDRESS = {
    addressName: 'SwitzerlandMobility Foundation',
    addressName2: 'Monbijoustrasse 61 - 3007 Bern',
    addressMobile: '+41 31 313 02 70',
};
let ElementContactForm = class ElementContactForm extends LitElement {
    constructor() {
        super(...arguments);
        this.overlayContent = false;
        this.message = '';
        this.secondName = '';
        this.firstName = '';
        this.email = '';
        this.phoneNumber = '';
        this.sendActive = false;
        this.captcha = '';
    }
    isShowBtnDisabled() {
        return (!this.captcha ||
            !this.message.length ||
            !this.firstName?.length ||
            !this.secondName?.length ||
            !isEmailValid(this.email));
    }
    cleanUpForm() {
        this.message = '';
        this.secondName = '';
        this.firstName = '';
        this.email = '';
        this.phoneNumber = '';
        this.fileUpload.deleteFile();
    }
    async send() {
        if (this.isShowBtnDisabled()) {
            return;
        }
        this.sendActive = true;
        const feedback = {
            captcha: this.captcha,
            message: this.message,
            firstName: this.firstName,
            secondName: this.secondName,
            email: this.email,
            file: this.file,
            phoneNumber: this.phoneNumber,
        };
        const response = await postFeedback(feedback);
        modal$.next(undefined);
        this.captchaWidget.reset();
        this.sendActive = false;
        if (response.ok) {
            if (!this.overlayContent)
                Router.go(createSeasonURL(getLanguageCode(), season$.value));
            const message = 'Your request has been sent.';
            this.dispatchEvent(new CustomEvent('alertnotif', {
                bubbles: true,
                detail: {
                    type: 'success',
                    message: message,
                },
            }));
        }
        else {
            this.dispatchEvent(new CustomEvent('alertnotif', {
                bubbles: true,
                detail: {
                    type: 'error',
                    message: 'Error: Request was not sent.',
                },
            }));
        }
        this.cleanUpForm();
    }
    cancel() {
        this.captchaWidget.reset();
        modal$.next(undefined);
        this.cleanUpForm();
    }
    updated(changedProps) {
        if ((changedProps.has('answer') || changedProps.has('copy')) &&
            this.email) {
            this.answerEmailInput.validate();
        }
    }
    render() {
        const lang = getLanguageCode();
        const guideUrl = 'guide';
        const guideText = 'guide';
        const guideLink = html `<a
      href="/${lang}/${guideUrl}"
      target="_blank"
      class="group inline-flex items-baseline overflow-hidden link-with-icon-in-context clickable-icon-with-text"
    >
      ${externalLinkSvg({
            classes: 'translate-y-2 md:translate-y-1 clickable-icon-in-group',
        })}
      ${guideText}</a
    >`;
        return html ` <div
      class="${classMap({
            'px-details-xxs': this.overlayContent,
            'sm:px-details-m': this.overlayContent,
        })} grid grid-cols-4 pb-details-s md:grid-cols-8 md:pb-details-l mdxx:grid-cols-12"
    >
      <div class="${STYLE.gridElement}">
        <h1 data-cy="place-title" class="h1 mb-4">Contact form</h1>
      </div>
      <div class="${STYLE.gridElement}">
        <element-address
          .item="${ADDRESS}"
          .bigOverlay="${true}"
        ></element-address>
        <div class="body flex flex-col gap-y-[14px] pt-[14px]">
          <p>
            We are continuing to enhance SwitzerlandMobility. Your opinion and feedback about our routes, the signalisation, our website or the app are most welcome. Please use our contact form to send us your comments.
          </p>
          <p>
            You can also use the contact form to report technical errors concerning the website or the app. We will take care of the problem as quickly as possible.
          </p>
          <p>
            In the meantime, you'll find helpful information in our
              ${guideLink}.
          </p>
          <p>Thank you in advance for your support!</p>
          <p>Your SwitzerlandMobility Foundation</p>
        </div>
      </div>
      <div class="col-span-full">
        ${divider('100%', '2px', '32px 0', '#495057')}
        <div class="${STYLE.gridContainer} gap-y-[24px]">
          <element-input
            class="${STYLE.gridElement}"
            .value="${this.message}"
            placeholder="Message"
            label=${'Message'}
            .textArea="${true}"
            .required="${true}"
            @input="${(evt) => {
            const target = evt.target;
            this.message = target.value;
        }}"
          >
          </element-input>
          <element-file-upload
            class="col-[1_/_span_4] xxl:col-[3_/_span_4]"
            label="Upload file or image"
            hint="Drag or click to add file or photo, max. 10 MB"
            @uploaded="${(evt) => (this.file = evt.detail.file)}"
            @deleted="${() => {
            this.file = undefined;
        }}"
          >
          </element-file-upload>
        </div>
      </div>
      <div class="col-span-full">
        ${divider('100%', '2px', '32px 0', '#495057')}
        <div class="${STYLE.gridContainer}">
          <div class="${STYLE.gridElement} body flex flex-col gap-y-[32px]">
            <div class="flex flex-col gap-y-[32px]">
              <div class="flex flex-col gap-y-[8px]">
                <div class="font-bold">Your contact information</div>
                <div>
                  We need your information in order to contact you.
                </div>
              </div>
              <div class="flex flex-col gap-y-[24px]">
                <div
                  class="flex flex-col gap-x-[16px] gap-y-[24px] sm:flex-row"
                >
                  <element-input
                    class="flex-1"
                    label="Name"
                    .required="${true}"
                    .value="${this.secondName}"
                    placeholder="Name"
                    @input="${(evt) => (this.secondName = evt.target.value)}"
                  >
                  </element-input>
                  <element-input
                    class="flex-1"
                    label="First name"
                    .required="${true}"
                    .value="${this.firstName}"
                    placeholder="First name"
                    @input="${(evt) => (this.firstName = evt.target.value)}"
                  >
                  </element-input>
                </div>
                <div
                  class="flex flex-col gap-x-[16px] gap-y-[24px] sm:flex-row"
                >
                  <element-input
                    class="answer-email-input flex-1"
                    label="Email address"
                    .required="${true}"
                    .type="email"
                    .value="${this.email}"
                    placeholder="Email address"
                    @input="${(evt) => (this.email = evt.target.value)}"
                  >
                  </element-input>
                  <element-input
                    class="flex-1"
                    label="Phone number"
                    .type="tel"
                    .value="${this.phoneNumber}"
                    placeholder="Phone number"
                    @input="${(evt) => (this.phoneNumber = (evt.target).value)}"
                  >
                  </element-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="col-span-full pt-[32px] md:col-[1_/_span_8] mdxx:col-[1_/_span_8] xxl:col-[3_/_span_6]"
      >
        <div class="frc-captcha h-logo-s sm:h-logo-m"></div>
      </div>
      <div class="col-span-full mt-[40px] flex justify-end gap-x-[16px]">
        <element-button
          .type=${'secondary'}
          .label="${() => 'Cancel'}"
          .hidden="${!this.overlayContent}"
          @click="${this.cancel}"
        ></element-button>
        <div
          class="${classMap({ 'cursor-not-allowed': this.isShowBtnDisabled() })}"
        >
          <element-button
            .disabled=${this.isShowBtnDisabled()}
            .loading="${this.sendActive}"
            .label="${() => 'Send'}"
            @click="${this.send}"
          ></element-button>
        </div>
      </div>
    </div>`;
    }
    firstUpdated() {
        const sdk = new FriendlyCaptchaSDK();
        this.captchaWidget = sdk.createWidget({
            element: this.querySelector('.frc-captcha'),
            sitekey: CAPTCHA_SITEKEY,
            startMode: 'auto',
            language: getLanguageCode(),
        });
        this.captchaWidget.addEventListener('frc:widget.complete', (e) => {
            this.captcha = e.detail.response;
        });
        this.captchaWidget.addEventListener('frc:widget.expire', () => {
            this.captcha = '';
        });
        this.captchaWidget.addEventListener('frc:widget.error', () => {
            this.captcha = '';
        });
    }
    createRenderRoot() {
        return this;
    }
};
__decorate([
    property({ type: Boolean })
], ElementContactForm.prototype, "overlayContent", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "message", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "secondName", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "firstName", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "email", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "phoneNumber", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "file", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "sendActive", void 0);
__decorate([
    state()
], ElementContactForm.prototype, "captcha", void 0);
__decorate([
    query('.answer-email-input')
], ElementContactForm.prototype, "answerEmailInput", void 0);
__decorate([
    query('element-file-upload')
], ElementContactForm.prototype, "fileUpload", void 0);
ElementContactForm = __decorate([
    customElement('element-contact-form')
], ElementContactForm);
export { ElementContactForm };
//# sourceMappingURL=element-contact-form.js.map